import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import CustomScrollArea from 'react-perfect-scrollbar';

import {
  AutoFocusZone,
  Box,
  Flex,
  FocusZoneTabbableElements,
  GridRowBehaviorProps,
  Input,
  Ref,
  SiteVariablesPrepared,
  Text,
  gridRowNestedBehavior
} from '@fluentui/react-northstar';

import { ICSSInJSStyle } from '@fluentui/styles';

import { useAccessibility } from '@fluentui/react-bindings';

import { getCode, keyboardKey } from '@fluentui/keyboard-key';

import { Draggable, Droppable } from 'react-beautiful-dnd';

import { TTextObject } from '@fluentui/react-teams/lib/esm/translations';

import {
  BoardItem,
  IPreparedBoardItem,
  IBoardItemCardLayout,
  IBoardItem,
} from './BoardItem';

import setMultiple from '@fluentui/react-teams/lib/esm/lib/setMultiple';

import AppCSS from './Board.module.css';
import { getCachedFeature } from '../../../components/shared/cache/FeatureCache';
import { PRODUCT_LICENSE } from '../../../components/shared/utils/constants';
import { TFunction } from 'i18next';
import { SkeletonBoardCard } from '../../../components/shared/components/Skeleton';
import { laneEdgeKeys, resolveAnchorAction, restoreScrollTop } from './boardScrollAnchor';
import { VIRTUALIZE_LANES } from './boardConfig';
import { VirtualLaneBody } from './VirtualLaneBody';

export interface IBoardLaneProps {
  lane?: TBoardLane;
  laneKey: string;
  last?: boolean;
  first?: boolean;
  addItemDialog?: JSX.Element;
  editItemDialog?: (boardItem: IBoardItem) => JSX.Element;
  preparedItems: IPreparedBoardItem[];
  t: TFunction<"translation", undefined>;
  rtl: boolean;
  boardItemCardLayout: IBoardItemCardLayout;
  placeholderPosition: TPlaceholderPosition;
  exitPendingLane?: (value: string) => void;
  moveLane?: (laneKey: string, delta: -1 | 1) => void;
  deleteLane?: (laneKey: string) => void;
  pending?: boolean;
  isSlaEnabled: boolean;
  onLoadMore?: () => void;
  onLoadPrevious?: () => void;
  hasMore?: boolean;
  hasPrevious?: boolean;
  isLaneLoading?: boolean;
  isLaneLoadingTop?: boolean;
  windowStart?: number;
  placeholderTargetIndex?: number | null;
}

export type TBoardLane = {
  title: TTextObject;
};

export type TBoardLanes = {
  [laneKey: string]: TBoardLane;
};

export type TPlaceholderPosition = null | [number, number, number, number];

const boardLaneBehavior = (props: GridRowBehaviorProps) => {
  return setMultiple(gridRowNestedBehavior(props), {
    'focusZone.props': {
      handleTabKey: FocusZoneTabbableElements.all,
      isCircularNavigation: true,
      shouldEnterInnerZone: (event: React.KeyboardEvent<HTMLElement>) =>
        getCode(event) === keyboardKey.Enter,
    },
    'attributes.root': {
      role: 'group',
      'data-is-focusable': true,
      tabIndex: -1,
    },
    'keyActions.root.focus.keyCombinations': [{ keyCode: keyboardKey.Escape }],
    'keyActions.root.ignore.keyCombinations': [
      { keyCode: keyboardKey.ArrowRight },
      { keyCode: keyboardKey.ArrowDown },
      { keyCode: keyboardKey.ArrowLeft },
      { keyCode: keyboardKey.ArrowUp },
    ],
  });
};

export const separatorStyles: ICSSInJSStyle = {
  position: 'relative',
  '&::after': {
    content: '""',
    display: 'block',
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    width: '1px',
  },
};

const Placeholder = ({ position }: { position: TPlaceholderPosition }) =>
  position && (
    <Box
      variables={({ colorScheme }: SiteVariablesPrepared) => ({
        backgroundColor: colorScheme.brand.background1,
        borderColor: colorScheme.brand.foreground3,
      })}
      styles={{
        left: position[0] + 'px',
        top: position[1] + 'px',
        width: position[2] + 'px',
        height: position[3] + 'px',
        position: 'absolute',
        borderRadius: '4px',
        borderWidth: '1px',
        zIndex: 0,
      }}
    />
  );

const laneFocusBorderStyles = {
  content: '""',
  display: 'block',
  position: 'absolute',
  borderStyle: 'solid',
  borderWidth: 0,
  top: 0,
  bottom: 0,
  left: '1px',
  right: '2px',
  borderRadius: '4px',
  pointerEvents: 'none',
};

export const BoardLane = (props: IBoardLaneProps) => {
  const {
    lane,
    preparedItems,
    t,
    laneKey,
    last,
    addItemDialog,
    editItemDialog,
    boardItemCardLayout,
    placeholderPosition,
    exitPendingLane,
    isSlaEnabled,
    onLoadMore,
    onLoadPrevious,
    hasMore,
    hasPrevious,
    isLaneLoading,
    isLaneLoadingTop,
  } = props;

  const [laneNode, setLaneNode] = useState<HTMLElement | null>(null);
  const [isAnalystAccess, setIsAnalystAccess] = useState(false);

  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const anchorRef = useRef<{ id: string; top: number } | null>(null);
  const prevEdgeKeysRef = useRef(laneEdgeKeys(preparedItems));

  const captureAnchor = (el: HTMLElement, mode: 'top' | 'bottom') => {
    const containerRect = el.getBoundingClientRect();
    const nodes = Array.from(el.querySelectorAll<HTMLElement>('[data-rbd-draggable-id]'));
    let chosen: HTMLElement | null = null;
    for (const node of nodes) {
      const rect = node.getBoundingClientRect();
      const isVisible = rect.bottom > containerRect.top + 1 && rect.top < containerRect.bottom - 1;
      if (isVisible) {
        chosen = node;
        if (mode === 'top') break;
      }
    }
    anchorRef.current = chosen
      ? {
          id: chosen.getAttribute('data-rbd-draggable-id') ?? '',
          top: chosen.getBoundingClientRect().top - containerRect.top,
        }
      : null;
  };

  useLayoutEffect(() => {
    const el = scrollContainerRef.current;
    const nextEdgeKeys = laneEdgeKeys(preparedItems);
    const action = resolveAnchorAction(!!anchorRef.current, prevEdgeKeysRef.current, nextEdgeKeys);

    if (action === 'restore' && el && anchorRef.current) {
      const node = el.querySelector<HTMLElement>(
        `[data-rbd-draggable-id="${anchorRef.current.id}"]`
      );
      if (node) {
        const currentTop = node.getBoundingClientRect().top - el.getBoundingClientRect().top;
        el.scrollTop = restoreScrollTop(el.scrollTop, anchorRef.current.top, currentTop);
      }
      anchorRef.current = null;
    } else if (action === 'clear') {
      anchorRef.current = null;
    }
    prevEdgeKeysRef.current = nextEdgeKeys;
  });

  const getA11Props = useAccessibility(boardLaneBehavior, {
    actionHandlers: {
      preventDefault: (event) => {
        // preventDefault only if event coming from inside the lane
        if (event.currentTarget !== event.target) {
          event.preventDefault();
        }
      },

      focus: (event) => {
        if (laneNode && event.target !== event.currentTarget) {
          laneNode.focus();
          event.stopPropagation();
        }
      },

      ignore: (event) => {
        event.preventDefault();
        event.stopPropagation();
      },
    },
  });

  const getIsAnalystAccess = async () => {
    const a = await getCachedFeature(PRODUCT_LICENSE.AnalystAccess);
    setIsAnalystAccess(a);
  }

  useEffect(() => {
    getIsAnalystAccess();
  }, [])

  return (
    <Ref innerRef={setLaneNode}>
      {getA11Props.unstable_wrapWithFocusZone(
        <Box
          {...getA11Props('root', {
            styles: {
              display: 'flex',
              flexFlow: 'column nowrap',
              minWidth: '25rem',
              maxWidth: '25rem',
              borderRight: '1px solid transparent',
              flex: '1 0 0',
              position: 'relative',
              ':focus': { outline: 'none' },
              '&::before': laneFocusBorderStyles,
              '&::after': laneFocusBorderStyles,
            },
            variables: ({ colorScheme }: SiteVariablesPrepared) => ({
              borderFocus: colorScheme.default.borderFocus,
              borderFocusWithin: colorScheme.default.borderFocusWithin,
            }),
            'aria-label': `${
              lane ? lane.title : t('ticket.ticket-board.lane-pending')
            }, ${t('ticket.ticket-board.board-lane-instructions')}`
          })}
        >
          {props.pending ? (
            <AutoFocusZone>
              <Input
                placeholder={t('ticket.ticket-board.name-lane')}
                onBlur={(e) => {
                  exitPendingLane!(e.target.value);
                }}
                onKeyDown={(e) => {
                  switch (e.key) {
                    case 'Escape':
                      return exitPendingLane!('');
                    case 'Enter':
                      return exitPendingLane!(
                        (e.target as HTMLInputElement).value
                      );
                  }
                }}
                fluid
                styles={{ padding: '.05rem 1.25rem .25rem 1.25rem' }}
              />
            </AutoFocusZone>
          ) : (
            <Flex>
              <Text
                weight='bold'
                content={lane ? lane.title : ''}
                style={{
                  flex: '1 0 auto',
                  padding: '.375rem 1.25rem .75rem 1.25rem',
                  fontSize: 'inherit',
                  margin: 'inherit',
                }}
                as='h1'
              />
              
            </Flex>
          )}
          <Box
            variables={({ colorScheme }: SiteVariablesPrepared) => ({
              backgroundColor: 'colorScheme.default.background2',
              separatorColor: colorScheme.default.border2,
            })}
            styles={{
              flex: '0 0 auto',
              padding: '0 1.25rem .75rem 1.25rem',
              ...(last ? {} : separatorStyles),
            }}
          >
            {addItemDialog}
          </Box>
          <Box
            variables={({ colorScheme }: SiteVariablesPrepared) => ({
              separatorColor: colorScheme.default.border2,
            })}
            styles={{
              flex: '1 0 0',
              overflow: 'hidden',
              ...(last ? {} : separatorStyles),
            }}
            className={AppCSS.tikitDroppable}
          >
            {VIRTUALIZE_LANES ? (
              <VirtualLaneBody
                laneKey={laneKey}
                preparedItems={preparedItems ?? []}
                isAnalystAccess={isAnalystAccess}
                isSlaEnabled={isSlaEnabled}
                boardItemCardLayout={boardItemCardLayout}
                t={t}
                editItemDialog={editItemDialog}
                isLaneLoading={isLaneLoading}
                hasMore={hasMore}
                onLoadMore={onLoadMore}
                placeholderPosition={placeholderPosition}
                placeholderTargetIndex={props.placeholderTargetIndex}
              />
            ) : (
            <Droppable droppableId={laneKey} isDropDisabled={!isAnalystAccess}>
              {(provided, snapshot) => (
                <CustomScrollArea
                  style={{ position: 'relative', minHeight: '100%', zIndex: 1, width: '100%', right: '2px', overflowAnchor: 'none' }}
                  containerRef={(container: HTMLElement) => {
                    provided.innerRef(container);
                    scrollContainerRef.current = container;
                  }}
                  onScrollY={(container: HTMLElement) => {
                    if (container.scrollTop <= 1 && hasPrevious && !isLaneLoadingTop) {
                      captureAnchor(container, 'top');
                      onLoadPrevious?.();
                      return;
                    }
                    if (!hasMore || isLaneLoading) return;
                    const distanceFromBottom =
                      container.scrollHeight - container.scrollTop - container.clientHeight;
                    if (distanceFromBottom <= 1) {
                      captureAnchor(container, 'bottom');
                      onLoadMore?.();
                    }
                  }}
                  {...provided.droppableProps}
                >
                  {preparedItems?.length
                    ? preparedItems.map(item => (
                        <Draggable
                          draggableId={item.itemKey}
                          key={`Board__DraggableItem__${item.itemKey}`}
                          index={item.order}
                          isDragDisabled={!isAnalystAccess}
                        >
                          {(provided, snapshot) => (
                            <Ref innerRef={provided.innerRef}>
                              <BoardItem
                                isSlaEnabled={isSlaEnabled}
                                isDragging={snapshot.isDragging}
                                draggableProps={provided.draggableProps}
                                dragHandleProps={provided.dragHandleProps!}
                                {...{
                                  item,
                                  t,
                                  boardItemCardLayout
                                }}
                                {...(editItemDialog && {
                                  editItemDialog: editItemDialog(item)
                                })}
                              />
                            </Ref>
                          )}
                        </Draggable>
                      ))
                    : null}
                  {provided.placeholder}
                  {snapshot.isDraggingOver && (
                    <Placeholder position={placeholderPosition} />
                  )}
                  {isLaneLoading && (
                    <>
                      <div style={{ margin: '0 0.75rem 0.5rem' }}>
                        <SkeletonBoardCard />
                      </div>
                      <div style={{ margin: '0 0.75rem 0.5rem' }}>
                        <SkeletonBoardCard />
                      </div>
                    </>
                  )}
                </CustomScrollArea>
              )}
            </Droppable>
            )}
          </Box>
        </Box>
      )}
    </Ref>
  );
};
